import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import type React from 'react';
import { type FC, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { isMacPlatform } from '../helpers/platform';
import { formatShortcut } from '../helpers/use-keyboard-shortcut';

export const defaultProductSearchPlaceholders = [
  'Search by name, e.g. Crown Royal',
  'Combine words to narrow results, e.g. Titos Vodka',
  'Search by item code or barcode',
  'Use "quotes" for exact match, e.g. "VS"',
];

type SearchInputProperties = {
  inputRef: React.ForwardedRef<HTMLInputElement>;
  placeholders?: string[];
  /**
   * When set, renders a platform-correct `<kbd>` hint at the right edge so users
   * can discover the shortcut without trial-and-error. Pass the same spec the
   * `useKeyboardShortcut` hook receives (e.g. `'meta+f'`).
   */
  shortcut?: string;
  /**
   * Drop the input's own border/ring/rounding and width cap so a parent can
   * own a single shared border (e.g. welding a result-count segment onto the
   * search bar). Default false — standalone callers are unaffected.
   */
  grouped?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const SearchInput: FC<SearchInputProperties> = ({
  inputRef,
  placeholders,
  shortcut,
  grouped = false,
  ...properties
}) => {
  const shortcutLabel = useMemo(
    () => (shortcut ? formatShortcut(shortcut, isMacPlatform()) : null),
    [shortcut]
  );
  const searchId = useId();
  const [focused, setFocused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const prefersReducedMotion = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    if (!placeholders || placeholders.length <= 1 || prefersReducedMotion.current) {
      return;
    }

    timerRef.current = setInterval(() => {
      setVisible(false);
      fadeTimerRef.current = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % placeholders.length);
        setVisible(true);
      }, 300);
    }, 10000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
      }
    };
  }, [placeholders]);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      properties.onFocus?.(event);
    },
    [properties.onFocus]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      properties.onBlur?.(event);
    },
    [properties.onBlur]
  );

  const hasValue = Boolean(properties.value);
  const showOverlay = placeholders && !focused && !hasValue;
  const currentPlaceholder = placeholders?.[currentIndex] ?? '';

  const handleClear = useCallback(() => {
    // Synthesize a change event so the controlled consumer (which only reads
    // event.target.value) resets its state without a separate onClear prop.
    properties.onChange?.({
      target: { value: '' },
      currentTarget: { value: '' },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  }, [properties.onChange]);

  const reserveRightSlot = Boolean(shortcutLabel || hasValue);

  return (
    <div
      className={
        grouped
          ? 'flex-1 min-w-0'
          : `w-full flex-1 max-w-lg ${placeholders ? 'min-w-0 sm:min-w-80 lg:min-w-96' : 'min-w-0'}`
      }
    >
      <label htmlFor={searchId} className="sr-only">
        Search
      </label>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className="size-5 text-icon" aria-hidden="true" />
        </div>
        <input
          id={searchId}
          name="search"
          autoComplete="off"
          className={`block w-full border-0 bg-white py-2 pl-10 text-gray-900 placeholder:text-gray-400 sm:text-sm sm:leading-6 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none ${
            grouped
              ? 'rounded-none focus:outline-none focus:ring-0'
              : 'rounded-sm shadow-sm ring-1 ring-inset ring-gray-400 focus:outline-none focus:ring-primary-600'
          } ${reserveRightSlot ? 'pr-14' : 'pr-3'}`}
          placeholder={placeholders ? undefined : 'Search...'}
          type="search"
          ref={inputRef}
          {...properties}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {showOverlay && (
          <span
            className={`pointer-events-none absolute inset-y-0 left-10 ${shortcutLabel ? 'right-14' : 'right-3'} flex items-center truncate text-gray-400 sm:text-sm transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
          >
            {currentPlaceholder}
          </span>
        )}
        {hasValue ? (
          <button
            type="button"
            aria-label="Clear search"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClear}
            className="absolute inset-y-0 right-2 my-auto flex h-6 w-6 items-center justify-center rounded-sm text-icon hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-600"
          >
            <XMarkIcon className="size-5" aria-hidden="true" />
          </button>
        ) : (
          shortcutLabel && (
            <kbd className="pointer-events-none absolute inset-y-0 right-2 my-auto h-fit rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-[11px] text-gray-500 font-sans">
              {shortcutLabel}
            </kbd>
          )
        )}
      </div>
    </div>
  );
};
